<template>
  <div class="account-picker">
    <div class="_accounts">
      <m-card-item
        v-for="account in accounts"
        :key="account.uid"
        :tooltip="accountMeta(account)"
        tooltip-placement="right"
        @click="switchAccount(account)"
      >
        <template #left>
          <m-avatar
            :src="account.companyImage?.getURL || ''"
            :text="account.displayName"
            :amount-chars="1"
            :size="20"
          />
        </template>
        <div class="_name">
          <span class="_company-name">{{ account.displayName }}</span>
          <m-tag
            v-if="account.pending"
            xs
            class="_pending-tag"
          >
            {{ t('accountPicker.invitationNotAccepted') }}
          </m-tag>
        </div>
        <template
          v-if="account.isCurrent || account.locked"
          #right
        >
          <m-icon
            v-if="account.isCurrent"
            type="check"
            :color="$colors.grey.darken4"
            size="14"
          />
          <m-icon
            v-else
            type="lock"
            :color="$colors.grey.base"
            size="14"
          />
        </template>
      </m-card-item>
    </div>
    <m-card-item
      icon="plus"
      :icon-color="$colors.blue.darken1"
      :color="$colors.blue.darken1"
      @click="emit('add-account')"
    >
      {{ t('accountPicker.addNewAccount') }}
    </m-card-item>
  </div>
</template>

<script setup>
import useAccountSwitch from '@/composables/account-switch';
import useMe from '@/composables/me';
import { camelCase } from 'lodash-es';
import { computed, onMounted } from 'vue';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['add-account']);

const { t } = useI18n();

const { currentTenant, reachableAccounts, refreshMe } = useMe();
const { switchAccount } = useAccountSwitch();

// The dropdown mounts this on open. Re-fetch /me from sentinel so account name, avatar and
// member count reflect edits made elsewhere this session (settings, invites) — those write
// nebula but not the /me snapshot this list renders from.
onMounted(refreshMe);

const accounts = computed(() => reachableAccounts.value.map((account) => ({
  ...account,
  displayName: account.companyName || t('accountPicker.untitledAccount'),
  isCurrent: account.uid === currentTenant.value?.accountId,
  pending: isNullOrUndefined(account.activatedAt),
  locked: account.accessible === false,
})));

const accountMeta = (account) => {
  const lines = [];
  const planId = account.accountSettings.planId;
  if (planId) {
    lines.push(t(`planHint.${camelCase(planId)}`));
  }
  lines.push(t('spaceTopBarMenu.members', { count: account.userCount ?? 0 }));
  return lines.join('\n');
};
</script>

<style scoped lang="scss" type="text/scss">
  .account-picker {
    ._accounts {
      max-height: 32rem;
      overflow-y: auto;
    }

    ._name {
      min-width: 0;
      display: flex;
      align-items: center;

      ._company-name {
        min-width: 0;
        max-width: 17rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ._pending-tag {
        margin-left: .6rem;
        flex-shrink: 0;
      }
    }
  }
</style>
